import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import NewReservation from './NewReservation';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaUser, FaHome, FaCalendarAlt, FaMoneyBillWave, FaEdit, FaTrash, FaEye, FaCheckCircle } from 'react-icons/fa';
import { FaTimesCircle } from 'react-icons/fa';
import { nightsBetween, formatDateSafe } from '../../../utils/dates';
import { decodeJWT } from '../../../utils/jwt';

const PageContainer = styled.div`
  padding: 40px;
  background: #f8f9fa;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

const WelcomeHeader = styled.div`
  background: white;
  padding: 20px 40px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 500;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const Tab = styled.button`
  padding: 12px 24px;
  background: ${props => props.active ? '#34574A' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    background: ${props => props.active ? '#2a4539' : '#f8f9fa'};
  }

  .count {
    display: inline-block;
    background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : '#eee'};
    padding: 2px 8px;
    border-radius: 12px;
    margin-left: 8px;
    font-size: 0.75rem;
  }
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    border-color: #ba824f;
    outline: none;
  }
`;

const ReservationsGrid = styled.div`
  display: grid;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const ReservationCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  ${props => props.$emAndamento && `
    border: 2px solid #ffe0b2;
    box-shadow: 0 4px 24px 0 rgba(245, 124, 0, 0.10);
    background: #fffaf3;
  `}
`;

const ReservationHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .reservation-id {
    font-size: 0.875rem;
    color: #666;
  }

  .status {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    ${props => props.$emAndamento && `
      background: #ffe0b2;
      color: #f57c00;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 6;
      font-size: 0.88rem;
      padding: 7px 16px;
      box-shadow: 0 2px 8px #ffe0b2ad;
    `}
  }
`;

const ReservationContent = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const ReservationInfo = styled.div`
  min-width: 0;

  h3 {
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  p {
    font-size: 0.875rem;
    color: #333;
    overflow-wrap: anywhere;
  }
`;

const ReservationActions = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: #34574A;
    color: white;
    border: none;

    &:hover {
      background: #2a4539;
    }
  }

  &.secondary {
    background: white;
    color: #666;
    border: 1px solid #eee;

    &:hover {
      background: #f8f9fa;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  width: 900px;
  max-width: 95vw;
  max-height: 90vh;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.25rem;
    color: #333;
    margin: 0;
  }

  .close-button {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 4px;
    font-size: 1.5rem;
    line-height: 1;
    
    &:hover {
      color: #333;
    }
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled.div`
  h3 {
    font-size: 0.875rem;
    color: #666;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const DetailItem = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 4px;
  }

  p {
    font-size: 0.875rem;
    color: #333;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.agendada {
    background: #e6f4ea;
    color: #1e7e34;
  }
  
  &.em-andamento {
    background: #fff3e0;
    color: #f57c00;
  }
  
  &.concluida {
    background: #e8eaf6;
    color: #3f51b5;
  }
  
  &.cancelada {
    background: #ffebee;
    color: #c62828;
  }
`;

const PrettyInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: #fafbfc;
  font-size: 1rem;
  color: #222;
  box-shadow: 0 1px 4px rgba(52,87,74,0.03);
  transition: border 0.2s, box-shadow 0.2s;
  margin-top: 6px;
  &:focus {
    border-color: #ba824f;
    background: #fff;
    outline: none;
    box-shadow: 0 2px 8px rgba(52,87,74,0.08);
  }
  &::placeholder {
    color: #bdbdbd;
    font-size: 0.97rem;
  }
`;

const PrettySelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: #fafbfc;
  font-size: 1rem;
  color: #222;
  box-shadow: 0 1px 4px rgba(52,87,74,0.03);
  margin-top: 6px;
  transition: border 0.2s, box-shadow 0.2s;
  appearance: none;
  &:focus {
    border-color: #ba824f;
    background: #fff;
    outline: none;
    box-shadow: 0 2px 8px rgba(52,87,74,0.08);
  }
`;

const PrettyDetailSection = styled(DetailSection)`
  background: #fafbfc;
  border-radius: 10px;
  padding: 22px 18px 18px 18px;
  margin-bottom: 0;
  box-shadow: 0 1px 8px rgba(52,87,74,0.04);
`;

const PrettyLabel = styled.label`
  font-size: 0.83rem;
  color: #888;
  margin-bottom: 3px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

const Reservations = () => {
  const navigate = useNavigate();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // handlers para modal de adicionar reserva
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };



  const [activeTab, setActiveTab] = useState('agendadas');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  // Código do usuário logado (JWT) — usado para saber se ele é hóspede ou anfitrião
  const [myCodigo] = useState(() => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = token ? decodeJWT(token) : null;
      return payload?.codigo || payload?.id || payload?.userId || null;
    } catch {
      return null;
    }
  });

  // True quando o usuário logado é apenas o hóspede da reserva
  // (não é hostRESERVA, dono do imóvel nem usuário da imobiliária)
  const isGuestOnly = (reservation) => {
    if (!myCodigo || !reservation) return false;
    const mine = String(myCodigo);
    const isGuest = String(reservation.usuarioRESERVA) === mine;
    const isHost =
      String(reservation.hostRESERVA) === mine ||
      String(reservation.imovel?.clienteIMOVEL ?? '') === mine ||
      String(reservation.imovel?.imobiliaria?.usuarioIMOBILIARIA ?? '') === mine;
    return isGuest && !isHost;
  };

  const statusMap = {
    1: 'agendada',
    2: 'em-andamento',
    3: 'concluida',
    4: 'cancelada'
  };

  // Converte valor monetário (string "2.542,00" ou número) em Number
  function parseMoney(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const raw = String(value).replace(/[^\d,.-]/g, '');
    if (!raw) return null;
    const numeric = raw.includes(',')
      ? Number(raw.replace(/\./g, '').replace(',', '.'))
      : Number(raw);
    return Number.isNaN(numeric) ? null : numeric;
  }

  // Retorna o valor total da reserva: usa o campo da API quando existir,
  // senão calcula noites x valor da diária do imóvel
  function getReservationTotal(reservation) {
    const apiValue =
      reservation.valorRESERVA ??
      reservation.valortotalRESERVA ??
      reservation.valorTotalRESERVA ??
      reservation.totalRESERVA;
    const parsed = parseMoney(apiValue);
    if (parsed !== null) return parsed;
    const noites = Number(reservation.noitesRESERVA) || nightsBetween(reservation.deRESERVA, reservation.ateRESERVA);
    const diaria = parseMoney(reservation.imovel?.valorIMOVEL);
    if (noites > 0 && diaria !== null) return noites * diaria;
    return null;
  }

  // Helper to format currency using the property's currency
  function formatCurrency(value, currencyISO) {
    if (value === null || value === undefined) return '';
    // Usa EUR como padrão se não houver moeda especificada (imóveis de Portugal)
    const iso = currencyISO || 'EUR';

    const numeric = typeof value === 'number' ? value : parseMoney(value);

    if (numeric === null || Number.isNaN(numeric)) return '';
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: iso
      }).format(numeric);
    } catch {
      // Se a moeda não for reconhecida pelo Intl, usa EUR
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'EUR'
      }).format(numeric);
    }
  }

  const statusOptions = [
    { value: 1, label: 'Agendada' },
    { value: 2, label: 'Em Andamento' },
    { value: 3, label: 'Concluída' },
    { value: 4, label: 'Cancelada' },
  ];

  const paymentStatusOptions = [
    { value: 1, label: 'Pendente' },
    { value: 2, label: 'Pago' },
    { value: 3, label: 'Cancelado' },
  ];

  const handleChangeStatus = async (reservation, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${import.meta.env.VITE_URL_API}/reservas/${reservation.codigoRESERVA}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...reservation, statusRESERVA: newStatus })
      });
      toast.success('Status atualizado com sucesso!', { position: 'top-center' });
      fetchReservations();
    } catch (err) {
      toast.error('Erro ao atualizar status!', { position: 'top-center' });
    }
  };

  const handleChangePaymentStatus = async (reservation, newPaymentStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${import.meta.env.VITE_URL_API}/reservas/${reservation.codigoRESERVA}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...reservation, statuspagamentoRESERVA: newPaymentStatus })
      });
      toast.success('Status de pagamento atualizado com sucesso!', { position: 'top-center' });
      fetchReservations();
    } catch (err) {
      toast.error('Erro ao atualizar status de pagamento!', { position: 'top-center' });
    }
  };

  const tabOptions = [
    { key: 'agendadas', label: 'Agendadas', status: 1 },
    { key: 'em-andamento', label: 'Em Andamento', status: 2 },
    { key: 'concluidas', label: 'Concluídas', status: 3 },
    { key: 'canceladas', label: 'Canceladas', status: 4 },
  ];

  const getTabCount = (status) =>
    reservations.filter(r => r.statusRESERVA === status).length;

  const filteredReservations = reservations.filter(reservation => {
    let matchesTab = false;
    if (activeTab === 'agendadas') matchesTab = reservation.statusRESERVA === 1;
    if (activeTab === 'em-andamento') matchesTab = reservation.statusRESERVA === 2;
    if (activeTab === 'concluidas') matchesTab = reservation.statusRESERVA === 3;
    if (activeTab === 'canceladas') matchesTab = reservation.statusRESERVA === 4;
    const nomeHospede = reservation.usuario?.nomeUSUARIO || String(reservation.usuarioRESERVA) || '';
    const matchesName = nomeHospede.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'todos' || reservation.statusRESERVA === Number(statusFilter);
    return matchesTab && matchesName && matchesStatus;
  });

  const totalPages = Math.ceil(filteredReservations.length / itemsPerPage);
  const paginatedReservations = filteredReservations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  const handleDeleteReservation = async (codigoRESERVA) => {
    const result = await Swal.fire({
      title: 'Excluir Reserva?',
      text: 'Tem certeza que deseja excluir esta reserva? Essa ação não poderá ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });
    if (!result.isConfirmed) return;
    try {
      await fetch(`${import.meta.env.VITE_URL_API}/reservas/${codigoRESERVA}`, { method: 'DELETE' });
      toast.success('Reserva excluída com sucesso!', { position: 'top-center' });
      fetchReservations();
    } catch (err) {
      toast.error('Erro ao excluir reserva!', { position: 'top-center' });
    }
  };

  const handleEditReservation = (reservation) => {
    setSelectedReservation(reservation);
    setDetailModalOpen(true);
  };

  const handleViewDetails = (reservation) => {
    navigate(`/dashboard/reservations/${reservation.codigoRESERVA}`);
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedReservation(null);
  };

// Normaliza os vários formatos de resposta possíveis da API em um array
const normalizeReservationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.reservas)) return payload.reservas;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// Garante statusRESERVA numérico válido (1-4). Valores ausentes/inesperados
// (ex.: reserva "pendente" gravada como 0) caem em Agendadas para não sumirem.
const normalizeStatus = (r) => {
  const s = Number(r?.statusRESERVA);
  return s >= 1 && s <= 4 ? s : 1;
};

const fetchReservations = useCallback(async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setReservations([]);
      return;
    }
    const headers = {
      accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    // Usa o endpoint /reservas/me que retorna as reservas do host logado
    const response = await fetch(`${import.meta.env.VITE_URL_API}/reservas/me?page=1&pageSize=100`, {
      headers,
    });
    if (!response.ok) throw new Error(`Erro ao buscar reservas: ${response.status}`);
    const data = await response.json();
    console.log('Resposta /reservas/me:', data);
    let list = normalizeReservationList(data);

    // Fallback: se /reservas/me vier vazio, busca todas as reservas e filtra
    // pelos imóveis do usuário logado ou pelo hostRESERVA (cobre o caso de a
    // reserva ter sido salva com hostRESERVA divergente do esperado)
    if (list.length === 0) {
      try {
        const payload = decodeJWT(token);
        const myCodigo = payload?.codigo || payload?.id || payload?.userId;
        const [imoveisResp, todasResp] = await Promise.all([
          fetch(`${import.meta.env.VITE_URL_API}/imoveis/me`, { headers }),
          fetch(`${import.meta.env.VITE_URL_API}/reservas`, { headers }),
        ]);
        const imoveisData = imoveisResp.ok ? await imoveisResp.json() : null;
        const todasData = todasResp.ok ? await todasResp.json() : null;
        const myImovelIds = new Set(
          (imoveisData?.imoveis || imoveisData?.items || [])
            .map(i => Number(i.codigoIMOVEL || i.id))
        );
        list = normalizeReservationList(todasData).filter(r =>
          myImovelIds.has(Number(r.imovelRESERVA)) ||
          (myCodigo && String(r.hostRESERVA) === String(myCodigo)) ||
          (myCodigo && String(r.usuarioRESERVA) === String(myCodigo))
        );
        console.log('Reservas via fallback /reservas:', list);
      } catch (fallbackErr) {
        console.error('Fallback de reservas falhou:', fallbackErr);
      }
    }

    setReservations(list.map(r => ({ ...r, statusRESERVA: normalizeStatus(r) })));
  } catch (err) {
    console.error(err);
    toast.error('Erro ao carregar reservas.', { position: 'top-center' });
    setReservations([]);
  }
}, []);

useEffect(() => {
  fetchReservations();
}, [fetchReservations]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'agendada': return 'Agendada';
      case 'em-andamento': return 'Em Andamento';
      case 'concluida': return 'Concluída';
      case 'cancelada': return 'Cancelada';
      default: return status;
    }
  };

  return (
    <PageContainer>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <ActionButton className="primary" onClick={handleOpenAddModal}>
          + Adicionar Reserva
        </ActionButton>
      </div>

      <WelcomeHeader>
        <h1>Minhas Reservas</h1>
      </WelcomeHeader>

      <TabsContainer>
        {tabOptions.map(tab => (
          <Tab
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="count">{getTabCount(tab.status)}</span>
          </Tab>
        ))}
      </TabsContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Buscar por nome do hóspede..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="todos">Todos os Status</option>
          <option value="1">Agendada</option>
          <option value="2">Em Andamento</option>
          <option value="3">Concluída</option>
          <option value="4">Cancelada</option>
        </FilterSelect>
      </FiltersContainer>

      <ReservationsGrid>
        {paginatedReservations && paginatedReservations.length > 0 ? paginatedReservations.map(reservation => (
          reservation && (
            <ReservationCard
              key={reservation.codigoRESERVA}
              $emAndamento={statusMap[reservation.statusRESERVA] === 'em-andamento'}
              style={statusMap[reservation.statusRESERVA] === 'concluida' ? {
                border: '1.5px solid #e3eaff',
                background: '#f7faff',
                boxShadow: '0 2px 8px #e3eaff40'
              } : statusMap[reservation.statusRESERVA] === 'cancelada' ? {
                border: '1.5px solid #ffd6db',
                background: '#fff7f8',
                boxShadow: '0 2px 8px #ffd6db40'
              } : {}}
              >
              <ReservationHeader $emAndamento={statusMap[reservation.statusRESERVA] === 'em-andamento'}>
                <span className="reservation-id">
                  {reservation.codigoRESERVA}
                  {isGuestOnly(reservation) && (
                    <span style={{
                      marginLeft: 8,
                      padding: '2px 10px',
                      borderRadius: 12,
                      background: '#eef2ff',
                      color: '#34574A',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}>
                      Sua reserva
                    </span>
                  )}
                </span>
                <span className={`status ${statusMap[reservation.statusRESERVA]}`}
                  style={statusMap[reservation.statusRESERVA] === 'concluida' ? {
                    background: 'transparent',
                    color: '#34574A',
                    fontWeight: 600,
                    fontSize: '0.93rem',
                    padding: 0,
                    border: 'none',
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  } : statusMap[reservation.statusRESERVA] === 'cancelada' ? {
                    background: 'transparent',
                    color: '#c62828',
                    fontWeight: 600,
                    fontSize: '0.93rem',
                    padding: 0,
                    border: 'none',
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  } : {}}
                >
                  {statusMap[reservation.statusRESERVA] === 'em-andamento' && <FaClock style={{ marginRight: 4 }} />}
                  {getStatusLabel(statusMap[reservation.statusRESERVA])}
                  {statusMap[reservation.statusRESERVA] === 'concluida' && (
                    <span style={{
                      marginLeft: 10,
                      display: 'flex',
                      alignItems: 'center',
                      background: '#e0f7e9',
                      color: '#219653',
                      borderRadius: 16,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      padding: '4px 12px',
                      boxShadow: '0 1px 4px #e0f7e980'
                    }}>
                      <FaCheckCircle style={{ marginRight: 6, color: '#219653', fontSize: 16 }} />
                      CPR Liberada
                    </span>
                  )}
                  {statusMap[reservation.statusRESERVA] === 'cancelada' && (
                    <span style={{
                      marginLeft: 10,
                      display: 'flex',
                      alignItems: 'center',
                      background: '#ffd6db',
                      color: '#c62828',
                      borderRadius: 16,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      padding: '4px 12px',
                      boxShadow: '0 1px 4px #ffd6db80'
                    }}>
                      <FaTimesCircle style={{ marginRight: 6, color: '#c62828', fontSize: 16 }} />
                      Reserva Cancelada
                    </span>
                  )}
                </span>
              </ReservationHeader>
              <ReservationContent style={{ rowGap: 10 }}>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaHome style={{ color: '#ba824f' }} /> Imóvel</h3>
                  <p>{reservation.imovel?.tituloIMOVEL || `ID: ${reservation.imovelRESERVA}`}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaUser style={{ color: '#34574A' }} /> Hóspede</h3>
                  <p>{reservation.usuario?.nomeUSUARIO || `ID: ${reservation.usuarioRESERVA}`}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaCalendarAlt style={{ color: '#6c63ff' }} /> Check-in</h3>
                  <p>{formatDateSafe(reservation.deRESERVA)}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaCalendarAlt style={{ color: '#ba824f' }} /> Check-out</h3>
                  <p>{formatDateSafe(reservation.ateRESERVA)}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaCalendarAlt style={{ color: '#34574A' }} /> Noites</h3>
                  <p>{reservation.noitesRESERVA ?? (nightsBetween(reservation.deRESERVA, reservation.ateRESERVA) || '-')}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaMoneyBillWave style={{ color: '#388e3c' }} /> Valor Diária</h3>
                  <p>{formatCurrency(reservation.imovel?.valorIMOVEL, reservation.imovel?.moedaIMOVEL) || '-'}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaMoneyBillWave style={{ color: '#2e7d32' }} /> Valor Total</h3>
                  <p>{formatCurrency(getReservationTotal(reservation), reservation.imovel?.moedaIMOVEL) || '-'}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaUser style={{ color: '#6c63ff' }} /> Contato</h3>
                  <p>{(() => {
                    const contatos = [reservation.whatsappRESERVA, reservation.usuario?.whatsappUSUARIO];
                    const whatsapp = contatos.find(v => v && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'null');
                    return whatsapp || reservation.usuario?.emailUSUARIO || '-';
                  })()}</p>
                </ReservationInfo>
                <ReservationInfo>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FaMoneyBillWave style={{ color: '#ba824f' }} /> Pagamento</h3>
                  <p>{reservation.statuspagamentoRESERVA === 1 ? 'Pendente' : reservation.statuspagamentoRESERVA === 2 ? 'Pago' : 'Cancelado'}</p>
                </ReservationInfo>
              </ReservationContent>
              <ReservationActions>
                {!isGuestOnly(reservation) && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.7rem', color: '#666', fontWeight: 500 }}>Status</label>
                        <select
                          value={reservation.statusRESERVA}
                          onChange={e => handleChangeStatus(reservation, Number(e.target.value))}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #eee', fontSize: 14 }}
                        >
                          {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '0.7rem', color: '#666', fontWeight: 500 }}>Pagamento</label>
                        <select
                          value={reservation.statuspagamentoRESERVA}
                          onChange={e => handleChangePaymentStatus(reservation, Number(e.target.value))}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid #eee',
                            fontSize: 14,
                            background: reservation.statuspagamentoRESERVA === 2 ? '#e8f5e9' : reservation.statuspagamentoRESERVA === 1 ? '#fff3e0' : '#ffebee',
                            color: reservation.statuspagamentoRESERVA === 2 ? '#2e7d32' : reservation.statuspagamentoRESERVA === 1 ? '#f57c00' : '#c62828',
                            fontWeight: 600
                          }}
                        >
                          {paymentStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <ActionButton className="secondary" onClick={() => handleEditReservation(reservation)} title="Editar">
                      <FaEdit style={{ marginRight: 4 }} /> Editar
                    </ActionButton>
                    <ActionButton className="primary" onClick={() => handleDeleteReservation(reservation.codigoRESERVA)} title="Excluir">
                      <FaTrash style={{ marginRight: 4 }} /> Excluir
                    </ActionButton>
                  </>
                )}
                <ActionButton onClick={() => handleViewDetails(reservation)} title="Ver Detalhes">
                  <FaEye style={{ marginRight: 4 }} /> Ver Detalhes
                </ActionButton>
              </ReservationActions>
            </ReservationCard>
          )
        )) : (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', padding: 40 }}>
            Nenhuma reserva encontrada.
          </div>
        )}
      </ReservationsGrid>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0', gap: 8 }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
          >Anterior</button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #eee',
                background: currentPage === i + 1 ? '#34574A' : '#fff',
                color: currentPage === i + 1 ? '#fff' : '#34574A',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >{i + 1}</button>
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #eee', background: '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
          >Próxima</button>
        </div>
      )}

      {isAddModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', minWidth: 600 }}>
            <button onClick={handleCloseAddModal} style={{ position: 'absolute', top: 8, right: 12, background: 'none', border: 'none', fontSize: 26, color: '#6c63ff', cursor: 'pointer', zIndex: 2 }}>&times;</button>
            <NewReservation onSuccess={() => { handleCloseAddModal(); fetchReservations(); }} />
          </div>
        </div>
      )}

      {detailModalOpen && selectedReservation && (
        <Modal isOpen={detailModalOpen}>
          <ModalContent>
            <ModalHeader>
              <h2>Detalhes da Reserva {selectedReservation.codigoRESERVA}</h2>
              <button className="close-button" onClick={handleCloseModal}>&times;</button>
            </ModalHeader>
            <ModalBody>
              <DetailGrid>
                <DetailSection>
                  <h3>Imóvel</h3>
                  <DetailItem>
                    <label>Título</label>
                    <p>{selectedReservation.imovel?.tituloIMOVEL || '-'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Endereço</label>
                    <p>{selectedReservation.imovel?.enderecoIMOVEL || '-'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Valor Diária</label>
                    <p>{formatCurrency(selectedReservation.imovel?.valorIMOVEL, selectedReservation.imovel?.moedaIMOVEL) || '-'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Status</label>
                    <p>{getStatusLabel(statusMap[selectedReservation.statusRESERVA]) || '-'}</p>
                  </DetailItem>
                </DetailSection>
                <DetailSection>
                  <h3>Hóspede</h3>
                  <DetailItem>
                    <label>Nome</label>
                    <p>{selectedReservation.usuario?.nomeUSUARIO || 'Não informado'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Email</label>
                    <p>{selectedReservation.usuario?.emailUSUARIO || 'Não informado'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Contacto Telefónico</label>
                    <p>
                      {(() => {
                        const prefixo = selectedReservation.usuario?.prefixoUSUARIO;
                        const numero = selectedReservation.usuario?.numeroprincipalUSUARIO;
                        if (prefixo && numero) {
                          return `+${prefixo} ${numero}`;
                        }
                        if (numero) {
                          return numero;
                        }
                        return 'Não informado';
                      })()}
                    </p>
                  </DetailItem>
                  <DetailItem>
                    <label>WhatsApp</label>
                    <p>
                      {(() => {
                        const whatsappUsuario = selectedReservation.usuario?.whatsappUSUARIO;
                        if (whatsappUsuario && whatsappUsuario !== 'null' && whatsappUsuario.trim() !== '') {
                          return whatsappUsuario;
                        }
                        return 'Não informado';
                      })()}
                    </p>
                  </DetailItem>
                </DetailSection>
                <DetailSection>
                  <h3>Datas & Pagamento</h3>
                  <DetailItem>
                    <label>Check-in</label>
                    <p>{formatDateSafe(selectedReservation.deRESERVA)}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Check-out</label>
                    <p>{formatDateSafe(selectedReservation.ateRESERVA)}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Noites</label>
                    <p>{selectedReservation.noitesRESERVA ?? (nightsBetween(selectedReservation.deRESERVA, selectedReservation.ateRESERVA) || '-')}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Valor Total</label>
                    <p>{formatCurrency(getReservationTotal(selectedReservation), selectedReservation.imovel?.moedaIMOVEL) || '-'}</p>
                  </DetailItem>
                  <DetailItem>
                    <label>Status Pagamento</label>
                    <p>{selectedReservation.statuspagamentoRESERVA === 1 ? 'Pendente' : selectedReservation.statuspagamentoRESERVA === 2 ? 'Pago' : 'Cancelado'}</p>
                  </DetailItem>
                </DetailSection>
              </DetailGrid>
            </ModalBody>
            <ReservationActions>
              <ActionButton className="secondary" onClick={handleCloseModal}>Fechar</ActionButton>
            </ReservationActions>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Reservations;